import { useCallback, useEffect, useRef, useState } from "react";
import { API_CALLS } from "../../../../../API/api_calls";
import { actions, useAppDispatch, useAppSelector } from "../../../../../redux";
import { MeProps } from "../../../../../redux/slices/me/Me";
import { MODAL_Z_INDEX } from "../../../../../utils";
import { Modal } from "../../../../Common";
import { FlexColumn } from "../../../../Common/Styles";
import { EmptySearchResult } from "./EmptySearchResult";
import { FollowersFollowingProps } from "./FollowersFollowing";
import { FollwerUserInfo } from "./FollwerUserInfo";
import { FollowersLoading } from "./Loading";
import { UnfollowUser } from "./UnfollowUser";
import { useFollowersFollowingEmptyState } from "./useFollowersFollowingEmptyState";
import { useObserver } from "./useObserver";

export const Following = ({
  filterText,
  searchInputId,
}: FollowersFollowingProps.Content) => {
  const dispatch = useAppDispatch();
  const followingRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<MeProps.BasicUserInfo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const {
    followingUsers,
    getFollowingUsers_API_STATUS,
    searchUsers,
    searchUsers_API_STATUS,
  } = useAppSelector((state) => state.connections);
  const { data: users } = followingUsers;
  const FollowingEmptyState = useFollowersFollowingEmptyState({
    title: "You're not following anyone.",
    iconType: "UserPlus",
  });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!followingRef.current) return;
      const usersList = filterText ? searchUsers.data : users;
      if (!usersList?.length) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < usersList.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          if (focusedIndex === 0) {
            const searchInput = document.getElementById(searchInputId);
            searchInput && (searchInput as HTMLElement).focus();
            setFocusedIndex(-1);
            break;
          }
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
      }
    },
    [filterText, searchUsers.data, users, focusedIndex, searchInputId]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (focusedIndex >= 0) {
      const element = followingRef.current?.children[
        focusedIndex
      ] as HTMLElement;
      element?.focus();
    }
  }, [focusedIndex]);

  //#region useEffects
  useEffect(() => {
    dispatch(actions.connections.resetFollowingUsers());
    API_CALLS.CONNECTIONS.getFollowingUsers(1);
    //eslint-disable-next-line
  }, []);
  useObserver(
    followingRef,
    () => {
      if (filterText) {
        if (!searchUsers.remainingCount) return;
        if (searchUsers_API_STATUS === "progress") return;
        API_CALLS.CONNECTIONS.searchUsers({
          ...searchUsers,
          query: filterText,
          type: "following",
        });
      } else {
        if (!followingUsers.remainingCount) return;
        if (getFollowingUsers_API_STATUS === "progress") return;
        API_CALLS.CONNECTIONS.getFollowingUsers(followingUsers.page);
      }
    },
    users.length + searchUsers.data.length
  );
  //#endregion

  if (!users || !users.length) {
    if (getFollowingUsers_API_STATUS === "progress")
      return <FollowersLoading />;
    return <FollowingEmptyState />;
  }
  if (filterText && !searchUsers.data.length) {
    const showLoading =
      searchUsers_API_STATUS === "idle" ||
      searchUsers_API_STATUS === "progress";

    if (showLoading) return <FollowersLoading />;
    return <EmptySearchResult />;
  }

  const usersList = filterText ? searchUsers.data : users;

  return (
    <>
      {modalOpen && (
        <Modal
          type="Pages"
          from="following-modal"
          setCloseButton={setModalOpen}
          showCloseButton={true}
          modalSize="small"
          closeModalOnClickOutside={modalOpen}
          maxWidth={352}
          minWidth={352}
          closeButtonTabIndex={3}
          zIndex={MODAL_Z_INDEX + 1}
        >
          <UnfollowUser
            showModal={modalOpen}
            user={user}
            onCancelClick={() => setModalOpen(false)}
            onConfirmClick={(id) => {
              API_CALLS.CONNECTIONS.unfollowAUser({
                id,
                onSuccess: () => setModalOpen(false),
              });
            }}
          />
        </Modal>
      )}
      <FlexColumn ref={followingRef} $gapInPx={8}>
        {usersList.map((user, index) => (
          <FollwerUserInfo
            key={index + user.id}
            type="following"
            user={user}
            index={index}
            onHover={setFocusedIndex}
            onClick={(user) => {
              setModalOpen(true);
              setUser(user);
            }}
          />
        ))}
      </FlexColumn>
    </>
  );
};
